/**
 * Studio — Rule Set detail: rules list, create draft, publish, diff, rollback (set current).
 */

import { useState } from "react";
import { Loader2, Upload, RotateCcw, GitCompare } from "lucide-react";
import { AppShell } from "../../layouts/AppShell";
import {
  useRuleSet,
  useRuleSetRules,
  useCurrentRuleSet,
} from "../../../api/opsV2/hooks";
import {
  createDraftFromPublished,
  publishRuleSet,
  setCurrentRuleSet,
  getRuleSetDiff,
  deleteRule,
} from "../../../api/opsV2/client";
import { getCurrentOrgId } from "../../../state/orgContext";

function getProductIdFromHash(): string | null {
  const hash = window.location.hash;
  const idx = hash.indexOf("?");
  if (idx === -1) return null;
  return new URLSearchParams(hash.slice(idx + 1)).get("product_id");
}

type StudioRuleSetDetailPageProps = {
  ruleSetId: string;
  productId?: string | null;
  userName?: string;
  onBack?: () => void;
};

export function StudioRuleSetDetailPage({
  ruleSetId,
  productId: productIdProp,
  userName = "Utilisateur",
  onBack,
}: StudioRuleSetDetailPageProps) {
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [rollbackError, setRollbackError] = useState<string | null>(null);
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffData, setDiffData] = useState<{
    only_in_a: string[];
    only_in_b: string[];
    changed: Array<{ rule_key: string; in_a: Record<string, unknown>; in_b: Record<string, unknown> }>;
  } | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const productId = productIdProp ?? getProductIdFromHash();

  const { data: ruleSet, loading: loadingRuleSet, refetch: refetchRuleSet } = useRuleSet(ruleSetId);
  const { data: rules, loading: loadingRules, refetch: refetchRules } = useRuleSetRules(ruleSetId);
  const { data: currentForProduct, refetch: refetchCurrent } =
    useCurrentRuleSet(productId ?? null);

  const isCurrent = currentForProduct?.id === ruleSetId;
  const canCreateDraft = ruleSet?.status === "published";
  const canPublish = ruleSet?.status === "draft";
  const canRollback = ruleSet?.status === "published" && currentForProduct && currentForProduct.id !== ruleSetId;
  const canShowDiff = ruleSet?.status === "draft" && currentForProduct?.id;
  const canEditRules = ruleSet?.status === "draft";
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null);

  const handleCreateDraft = async () => {
    if (!canCreateDraft) return;
    setDraftError(null);
    setCreatingDraft(true);
    try {
      const newDraft = await createDraftFromPublished(ruleSetId, getCurrentOrgId());
      if (newDraft?.id) {
        window.location.hash = `#/studio/rule-sets/${newDraft.id}${productId ? `?product_id=${productId}` : ""}`;
        return;
      }
      refetchRuleSet();
      refetchCurrent();
    } catch (e: unknown) {
      setDraftError(e instanceof Error ? e.message : "Erreur lors de la création du brouillon");
    } finally {
      setCreatingDraft(false);
    }
  };

  const handlePublish = async () => {
    if (!canPublish) return;
    setPublishError(null);
    setPublishing(true);
    try {
      await publishRuleSet(ruleSetId, {}, getCurrentOrgId());
      refetchRuleSet();
      refetchCurrent();
    } catch (e: unknown) {
      setPublishError(e instanceof Error ? e.message : "Publication refusée (validation).");
    } finally {
      setPublishing(false);
    }
  };

  const handleRollback = async () => {
    if (!canRollback) return;
    setRollbackError(null);
    setRollingBack(true);
    try {
      await setCurrentRuleSet(ruleSetId, {}, getCurrentOrgId());
      refetchRuleSet();
      refetchCurrent();
    } catch (e: unknown) {
      setRollbackError(e instanceof Error ? e.message : "Impossible de définir comme courant.");
    } finally {
      setRollingBack(false);
    }
  };

  const handleShowDiff = async () => {
    if (!canShowDiff || !currentForProduct?.id) return;
    setDiffLoading(true);
    setDiffOpen(true);
    setDiffData(null);
    try {
      const d = await getRuleSetDiff(ruleSetId, currentForProduct.id, getCurrentOrgId());
      setDiffData(d);
    } catch {
      setDiffData({ only_in_a: [], only_in_b: [], changed: [] });
    } finally {
      setDiffLoading(false);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!canEditRules || !window.confirm("Supprimer cette règle ?")) return;
    setDeletingRuleId(ruleId);
    try {
      await deleteRule(ruleSetId, ruleId, getCurrentOrgId());
      refetchRules();
    } finally {
      setDeletingRuleId(null);
    }
  };

  if (loadingRuleSet) {
    return (
      <AppShell userName={userName} topbar={<div className="text-sm text-muted">Rule Set</div>}>
        <div className="ds-container py-6 flex items-center gap-2 text-sm text-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement…
        </div>
      </AppShell>
    );
  }
  if (!ruleSet) {
    return (
      <AppShell userName={userName} topbar={<div className="text-sm text-muted">Rule Set</div>}>
        <div className="ds-container py-6">
          <p className="text-sm text-muted">Rule Set introuvable.</p>
          {onBack && (
            <button type="button" onClick={onBack} className="mt-2 text-sm text-primary hover:underline">
              Retour à la liste
            </button>
          )}
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      userName={userName}
      topbar={
        <div className="flex items-center gap-4">
          {onBack && (
            <button type="button" onClick={onBack} className="text-sm text-muted hover:text-text">
              Rule Sets
            </button>
          )}
          <span className="text-sm font-medium text-text">
            {ruleSet.name ?? `Rule set v${ruleSet.version_no}`}
          </span>
          <span
            className={
              ruleSet.status === "published"
                ? "rounded bg-success/20 px-1.5 py-0.5 text-xs text-success"
                : "rounded bg-muted px-1.5 py-0.5 text-xs text-muted"
            }
          >
            {ruleSet.status}
          </span>
        </div>
      }
    >
      <div className="ds-container py-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-text">
              {ruleSet.name ?? `Rule set v${ruleSet.version_no}`}
            </h1>
            <p className="text-sm text-muted mt-1">
              Version {ruleSet.version_no} · Produit {ruleSet.product_id.slice(0, 8)}…
              {ruleSet.published_at &&
                ` · Publié le ${new Date(ruleSet.published_at).toLocaleDateString("fr-CA", { dateStyle: "medium" })}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canPublish && (
              <button
                type="button"
                onClick={handlePublish}
                disabled={publishing}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
              >
                {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Publier
              </button>
            )}
            {canRollback && (
              <button
                type="button"
                onClick={handleRollback}
                disabled={rollingBack}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border bg-surface text-sm font-medium hover:bg-muted/50 disabled:opacity-50"
              >
                {rollingBack ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                Définir comme courant (rollback)
              </button>
            )}
            {canShowDiff && (
              <button
                type="button"
                onClick={handleShowDiff}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border bg-surface text-sm font-medium hover:bg-muted/50"
              >
                <GitCompare className="h-4 w-4" />
                Voir le diff
              </button>
            )}
            {canCreateDraft && (
              <button
                type="button"
                onClick={handleCreateDraft}
                disabled={creatingDraft}
                className="px-4 py-2 rounded-md border border-border bg-surface text-sm font-medium hover:bg-muted/50 disabled:opacity-50"
              >
                {creatingDraft ? (
                  <>
                    <Loader2 className="inline h-4 w-4 animate-spin mr-2" />
                    Création…
                  </>
                ) : (
                  "Créer un brouillon"
                )}
              </button>
            )}
          </div>
        </div>
        {isCurrent && (
          <p className="text-xs text-success mb-4">Ce Rule Set est le Rule Set courant pour ce produit.</p>
        )}
        {draftError && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive mb-4">
            {draftError}
          </div>
        )}
        {publishError && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive mb-4">
            {publishError}
          </div>
        )}
        {rollbackError && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive mb-4">
            {rollbackError}
          </div>
        )}
        {diffOpen && (
          <div className="rounded-lg border border-border bg-muted/20 p-4 mb-6">
            <h3 className="text-sm font-medium text-text mb-2">Diff (brouillon vs courant)</h3>
            {diffLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted">
                <Loader2 className="h-4 w-4 animate-spin" />
                Chargement…
              </div>
            ) : diffData ? (
              <div className="text-sm space-y-2">
                {diffData.only_in_a.length > 0 && (
                  <p className="text-text">Uniquement dans ce brouillon : {diffData.only_in_a.join(", ")}</p>
                )}
                {diffData.only_in_b.length > 0 && (
                  <p className="text-muted">Uniquement dans le courant : {diffData.only_in_b.join(", ")}</p>
                )}
                {diffData.changed.length > 0 && (
                  <p className="text-text">Modifiées : {diffData.changed.map((c) => c.rule_key).join(", ")}</p>
                )}
                {diffData.only_in_a.length === 0 && diffData.only_in_b.length === 0 && diffData.changed.length === 0 && (
                  <p className="text-muted">Aucune différence.</p>
                )}
              </div>
            ) : null}
            <button
              type="button"
              onClick={() => setDiffOpen(false)}
              className="mt-2 text-xs text-muted hover:text-text"
            >
              Fermer
            </button>
          </div>
        )}

        <h2 className="text-sm font-medium text-text mb-2">Règles</h2>
        {loadingRules ? (
          <div className="flex items-center gap-2 text-sm text-muted py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement…
          </div>
        ) : !rules || rules.length === 0 ? (
          <p className="text-sm text-muted py-4">Aucune règle.</p>
        ) : (
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left p-3 font-medium text-text">Clé</th>
                  <th className="text-left p-3 font-medium text-text">Template</th>
                  <th className="text-left p-3 font-medium text-text w-28">Déclenchement</th>
                  <th className="text-left p-3 font-medium text-text w-24">Obligatoire</th>
                  <th className="text-left p-3 font-medium text-text w-24">Origine</th>
                  {canEditRules && <th className="text-right p-3 font-medium text-text w-24">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} className="border-b border-border/50 hover:bg-muted/20">
                    <td className="p-3 font-medium text-text">{r.rule_key}</td>
                    <td className="p-3 text-muted">{r.process_template_id.slice(0, 8)}…</td>
                    <td className="p-3 text-muted">{r.start_policy}</td>
                    <td className="p-3 text-muted">{r.required ? "Oui" : "Optionnel"}</td>
                    <td className="p-3 text-muted">{r.origin_type}</td>
                    {canEditRules && (
                      <td className="p-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleDeleteRule(r.id)}
                          disabled={deletingRuleId === r.id}
                          className="text-xs text-destructive hover:underline disabled:opacity-50"
                        >
                          {deletingRuleId === r.id ? "…" : "Supprimer"}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
